import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, Grid, Chip,
  CircularProgress, Alert, Button, Table, TableHead, TableBody, TableRow, TableCell, Autocomplete, TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { get, post } from '../../utils/api';
import { customers } from '../../services/kioskApi';

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const ACCT = { icct: 'ICCT', kotilinga: 'Kotilinga' };
const custName = (c) => (c ? `${c.first_name || c.billing_first_name || ''} ${c.last_name || c.billing_last_name || ''}`.trim() || c.name || `#${c.id || c.customer_id}` : '');

// IdentityCard — a labelled who-is-this box (Authorize.Net payer, or portal devotee).
function IdentityCard({ title, name, email, tone = 'default', children }) {
  const border = tone === 'accent' ? 'primary.main' : 'divider';
  return (
    <Box sx={{ p: 1.5, border: 2, borderColor: border, borderRadius: 1.5, height: '100%', bgcolor: tone === 'accent' ? 'action.hover' : 'background.paper' }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>{title}</Typography>
      <Typography variant="body1" sx={{ fontWeight: 700 }}>{name || '—'}</Typography>
      {email ? <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>{email}</Typography> : null}
      {children}
    </Box>
  );
}

export default function CreateOrderDialog({ open, row, onClose, onCreated }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null); // chosen devotee {id,name,email}
  const [changing, setChanging] = useState(false); // show the devotee picker
  const [opts, setOpts] = useState([]);
  const [dup, setDup] = useState(null);

  const load = useCallback(async () => {
    if (!row) return;
    setLoading(true); setErr(''); setPreview(null); setSelected(null); setChanging(false); setDup(null);
    try {
      const p = await get('/api/manage/authnet-reconcile/create-preview', { account: row.account, trans_id: row.trans_id });
      setPreview(p);
      if (p.mapping?.customer_id) setSelected({ id: p.mapping.customer_id, name: p.mapping.customer_name, email: p.mapping.customer_email });
      setDup(p.duplicate || null);
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Could not load preview'); }
    finally { setLoading(false); }
  }, [row]);
  useEffect(() => { if (open) load(); }, [open, load]);

  // re-check duplicate whenever the selected devotee changes
  useEffect(() => {
    if (!open || !preview || !selected?.id) return undefined;
    let cancel = false;
    (async () => {
      try { const r = await get('/api/manage/authnet-reconcile/dup-check', { customer_id: selected.id, amount: preview.amount, date: preview.date, exclude_trans_id: preview.trans_id }); if (!cancel) setDup(r?.duplicate || null); }
      catch { if (!cancel) setDup(null); }
    })();
    return () => { cancel = true; };
  }, [selected?.id, open, preview]);

  const searchDevotees = async (q) => {
    if (!q || q.length < 2) { setOpts([]); return; }
    try { const list = await customers.searchUsers(q); setOpts(Array.isArray(list) ? list.slice(0, 10) : []); } catch { setOpts([]); }
  };

  const splits = useMemo(() => (preview?.mapping?.splits || []), [preview]);
  const prodSum = useMemo(() => Math.round(splits.reduce((a, s) => a + Number(s.amount || 0), 0) * 100) / 100, [splits]);
  const charge = Number(preview?.amount || 0);
  const fee = Math.round((charge - prodSum) * 100) / 100;
  const over = fee < -0.005;
  const noMapping = preview && !preview.mapping;
  const changed = selected && preview?.mapping && Number(selected.id) !== Number(preview.mapping.customer_id);
  const anetName = preview?.anet?.name || preview?.donor_name || '';
  const nameMismatch = selected && anetName && custName({ name: selected.name }).toLowerCase() !== anetName.toLowerCase();

  const doCreate = async () => {
    setCreating(true); setErr('');
    try {
      const r = await post('/api/manage/authnet-reconcile/create-one', { trans_id: preview.trans_id, account: preview.account, customer_id: selected?.id });
      onCreated?.(r);
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Could not create the order'); }
    finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onClose={() => !creating && onClose?.()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Create order from subscription
        <IconButton onClick={() => onClose?.()} disabled={creating} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          : err && !preview ? <Alert severity="error">{err}</Alert>
            : preview ? (
              <>
                <Grid container spacing={1} sx={{ mb: 1.5 }}>
                  <Grid item xs={4}><Typography variant="caption" color="text.secondary" display="block">Account</Typography><Typography variant="body2">{ACCT[preview.account] || preview.account}</Typography></Grid>
                  <Grid item xs={4}><Typography variant="caption" color="text.secondary" display="block">Date</Typography><Typography variant="body2">{fmtDate(preview.date)}</Typography></Grid>
                  <Grid item xs={4}><Typography variant="caption" color="text.secondary" display="block">Amount</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtMoney(preview.amount)}</Typography></Grid>
                  <Grid item xs={12}><Typography variant="caption" color="text.secondary" display="block">Subscription</Typography><Typography variant="body2">{preview.subscription_id} · pay {preview.pay_num ?? '?'}</Typography></Grid>
                </Grid>

                {/* WHO: Authorize.Net payer vs the portal devotee the order books to */}
                <Grid container spacing={1.5} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <IdentityCard title="Authorize.Net (payer)" name={anetName} email={preview.anet?.email} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <IdentityCard title={`Portal devotee · order books to${changed ? ' (changed)' : ''}`} name={selected ? custName({ name: selected.name }) : (noMapping ? 'none' : '—')} email={selected?.email} tone="accent">
                      <Button size="small" startIcon={<SwapHorizIcon />} onClick={() => setChanging((v) => !v)} sx={{ mt: 0.5, px: 0.5 }}>{changing ? 'Keep' : 'Change devotee'}</Button>
                    </IdentityCard>
                  </Grid>
                </Grid>

                {nameMismatch && (
                  <Alert severity="info" sx={{ mb: 1 }}>The payer name on Authorize.Net (<b>{anetName}</b>) differs from the portal devotee (<b>{custName({ name: selected.name })}</b>). This can be fine (family/spouse), but verify it's the right devotee.</Alert>
                )}

                {changing && (
                  <Box sx={{ mb: 1.5 }}>
                    <Autocomplete
                      options={opts}
                      getOptionLabel={(o) => `${custName(o)}${o.email ? ` · ${o.email}` : ''}`}
                      onInputChange={(e, v) => searchDevotees(v)}
                      onChange={(e, v) => { if (v) { setSelected({ id: v.id, name: custName(v), email: v.email || v.billing_email || null }); setChanging(false); } }}
                      filterOptions={(x) => x}
                      renderInput={(params) => <TextField {...params} size="small" label="Search devotee by name or email" autoFocus />}
                    />
                    {(preview.suggestions || []).length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">Suggested:</Typography>
                        {preview.suggestions.slice(0, 4).map((s) => (
                          <Chip key={s.customer_id} size="small" label={`${s.name}${s.confidence === 'high' ? ' ✓' : ''}`} onClick={() => { setSelected({ id: s.customer_id, name: s.name, email: s.email }); setChanging(false); }} />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {dup && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    A matching order already exists for this devotee: <b>{`#${dup.order_number || dup.id}`}</b> on {fmtDate(dup.dt)} for {fmtMoney(dup.total_num)}. Creating may be a <b>duplicate</b> — consider linking instead.
                  </Alert>
                )}

                {noMapping ? (
                  <Alert severity="warning">This subscription isn’t mapped, so there’s no allocation to book. Map it first (Subscriptions tab) or use Post Payment.</Alert>
                ) : (
                  <>
                    <Table size="small" sx={{ mt: 1 }}>
                      <TableHead><TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 600 } }}><TableCell>Product</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
                      <TableBody>
                        {splits.map((s, i) => (<TableRow key={i}><TableCell>{s.label}</TableCell><TableCell align="right">{fmtMoney(s.amount)}</TableCell></TableRow>))}
                        {fee > 0.005 && (<TableRow><TableCell sx={{ color: 'text.secondary' }}>Processing fee</TableCell><TableCell align="right" sx={{ color: 'text.secondary' }}>{fmtMoney(fee)}</TableCell></TableRow>)}
                        <TableRow><TableCell sx={{ fontWeight: 700, borderBottom: 0 }}>Total (charged)</TableCell><TableCell align="right" sx={{ fontWeight: 700, borderBottom: 0 }}>{fmtMoney(charge)}</TableCell></TableRow>
                      </TableBody>
                    </Table>
                    {over && <Alert severity="warning" sx={{ mt: 1.5 }}>Allocation ({fmtMoney(prodSum)}) exceeds the charge ({fmtMoney(charge)}). Fix the mapping before creating.</Alert>}
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1.5 }}>
                      Books back-dated to {fmtDate(preview.date)}, no email sent.{changed ? ' Devotee changed from the mapping for this order only.' : ''}
                    </Typography>
                  </>
                )}
                {err && <Alert severity="error" sx={{ mt: 1.5 }}>{err}</Alert>}
              </>
            ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.()} disabled={creating}>Cancel</Button>
        <Button variant="contained" onClick={doCreate} disabled={creating || loading || over || noMapping || !selected}
          startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}>
          {dup ? 'Create anyway' : 'Create order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
